import { createColumnHelper } from '@tanstack/react-table';
import { Link } from 'react-router';

import { MeasurementDTO } from '@invenco/common-interface/shared';

import { Badge } from '@/components/core/badge';
import { Button } from '@/components/core/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/core/tabs';
import { DataTable, useDataTable } from '@/components/data-table';
import { formatDate, formatNumber } from '@/shared/helpers';

import { CreatedRecord, ErrorRecord, TemplateKey } from './types';

const createdColumnHelper = createColumnHelper<CreatedRecord>();
const errorColumnHelper = createColumnHelper<ErrorRecord>();

const createViewColumn = (urlPrefix: string) =>
  createdColumnHelper.display({
    id: 'view',
    cell: ({ row }) => (
      <Button variant="link" asChild>
        <Link to={`${urlPrefix}/${row.id}`} target="_blank">
          View
        </Link>
      </Button>
    ),
    meta: { align: 'right' },
  });

const createdColumns = {
  [TemplateKey.SKUS]: [
    createdColumnHelper.accessor('name', {
      header: 'SKU',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Badge variant="secondary">{row.original.name}</Badge>
          {row.original.description}
        </div>
      ),
    }),
    createdColumnHelper.accessor('barcode', { header: 'Barcode' }),
    createdColumnHelper.accessor('measurement', {
      id: 'dimensions',
      header: 'Dimensions (LxWxH)',
      cell: ({ getValue }) => {
        const value = getValue<MeasurementDTO | null>();
        return value ? `${value.length}x${value.width}x${value.height}cm` : '';
      },
      meta: { align: 'right' },
    }),
    createdColumnHelper.accessor('measurement', {
      id: 'weight',
      header: 'Weight',
      cell: ({ getValue }) => {
        const value = getValue<MeasurementDTO | null>();
        return value ? `${value.weight}kg` : '';
      },
      meta: { align: 'right' },
    }),
    createViewColumn('/products/skus'),
  ],
  [TemplateKey.ASNS]: [
    createdColumnHelper.accessor('name', { header: 'Name' }),
    createdColumnHelper.accessor('reference', { header: 'Reference' }),
    createdColumnHelper.accessor('expectedAt', {
      header: 'Date Expected',
      cell: ({ getValue }) => {
        const value = getValue<string | null>();
        return value ? formatDate(value) : '';
      },
    }),
    createdColumnHelper.accessor('status', { header: 'Status' }),
    createViewColumn('/supply/asns'),
  ],
  [TemplateKey.ORDERS]: [
    createdColumnHelper.accessor('name', { header: 'Name' }),
    createViewColumn('/sales/orders'),
  ],
};

const errorColumns = {
  [TemplateKey.SKUS]: [
    errorColumnHelper.accessor('input', {
      id: 'sku',
      header: 'SKU',
      cell: ({ getValue }) => (
        <div className="flex gap-2">
          <Badge variant="secondary">{getValue().name}</Badge>
          {getValue().description}
        </div>
      ),
    }),
    errorColumnHelper.accessor((row) => row.input.barcode, { id: 'barcode', header: 'Barcode' }),
    errorColumnHelper.accessor('error', { header: 'Error' }),
  ],
  [TemplateKey.ASNS]: [
    errorColumnHelper.accessor((row) => row.input.reference, {
      id: 'reference',
      header: 'Reference',
    }),
    errorColumnHelper.accessor('error', { header: 'Error' }),
  ],
  [TemplateKey.ORDERS]: [
    errorColumnHelper.accessor((row) => row.input.name, { id: 'name', header: 'Order name' }),
    errorColumnHelper.accessor((row) => row.input.currency, { id: 'currency', header: 'Currency' }),
    errorColumnHelper.accessor('error', { header: 'Error' }),
  ],
};

type Props = {
  template: TemplateKey;
  created: CreatedRecord[];
  errors: ErrorRecord[];
};

export function RunnerResultsTable({ template, created, errors }: Props) {
  const createdTable = useDataTable({
    columns: createdColumns[template],
    data: created,
    enableSorting: false,
  });
  const errorsTable = useDataTable({
    columns: errorColumns[template],
    data: errors,
    enableSorting: false,
  });
  return (
    <Tabs defaultValue="created">
      <TabsList>
        <TabsTrigger value="created" className="flex gap-2">
          Imported
          <Badge className="bg-border px-1 text-xs leading-none text-secondary-foreground">
            {formatNumber(created.length)}
          </Badge>
        </TabsTrigger>
        <TabsTrigger value="errors" className="flex gap-2">
          Errors
          <Badge className="bg-border px-1 text-xs leading-none text-secondary-foreground">
            {formatNumber(errors.length)}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="created">
        <DataTable table={createdTable} />
      </TabsContent>
      <TabsContent value="errors">
        <DataTable table={errorsTable} />
      </TabsContent>
    </Tabs>
  );
}
